import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NameResult } from '../types/NameResult';
import { useFavoritesContext } from '../context/FavoritesContext';
import { HiOutlineStar, HiStar } from 'react-icons/hi2';
import { LuCopy, LuCheck, LuEllipsis } from 'react-icons/lu';
import { checkDomain } from '../services/api';
import { NameScoreModal } from './NameScoreModal';
import { OverflowMenu } from './OverflowMenu';
import posthog from 'posthog-js';

interface NameResultCardProps {
  result: NameResult;
  index?: number;
  listView?: boolean;
  selectedTlds: string[];
  onUpdate: (patch: Partial<NameResult>) => void;
  onDismiss: () => void;
}

function normalizeScore(totalScore: number): number {
  return Math.round((totalScore / 40) * 100);
}

function scoreColor(pct: number): string {
  if (pct >= 85) return 'text-green-400';
  if (pct >= 70) return 'text-indigo-400';
  if (pct >= 55) return 'text-amber-400';
  return 'text-gray-500';
}

function scoreBarColor(pct: number): string {
  if (pct >= 85) return 'bg-green-500';
  if (pct >= 70) return 'bg-indigo-500';
  if (pct >= 55) return 'bg-amber-400';
  return 'bg-gray-600';
}

function UsptoStatus({ available }: { available: boolean | null | undefined }) {
  if (available === true) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-green-400">
        <span className="w-3.5 h-3.5 rounded-full border-2 border-green-400 flex items-center justify-center flex-shrink-0">
          <svg className="w-2 h-2" fill="none" viewBox="0 0 8 6">
            <path d="M1 3l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        No USPTO conflicts
      </span>
    );
  }
  if (available === false) {
    return (
      <span className="flex items-center gap-1.5 text-xs text-red-400">
        <span className="w-3.5 h-3.5 rounded-full border-2 border-red-400 flex items-center justify-center flex-shrink-0">
          <svg className="w-2 h-2" fill="none" viewBox="0 0 8 8">
            <path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </span>
        Registered trademark
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 text-xs text-amber-400">
      <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 16 16">
        <path d="M8 2L1 14h14L8 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M8 6v4M8 11.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
      Similar marks found
    </span>
  );
}

export const NameResultCard: React.FC<NameResultCardProps> = ({
  result,
  index = 0,
  listView = false,
  selectedTlds,
  onUpdate,
  onDismiss,
}) => {
  const { isFavorite, toggleFavorite } = useFavoritesContext();
  const favorited = isFavorite(result.name);

  const [activeTld, setActiveTld] = useState(selectedTlds[0] ?? '.com');
  const [tldAvailability, setTldAvailability] = useState<Record<string, boolean | null>>({});
  const [copied, setCopied] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [overflowOpen, setOverflowOpen] = useState(false);

  useEffect(() => {
    if (!selectedTlds.includes(activeTld)) {
      setActiveTld(selectedTlds[0] ?? '.com');
    }
  }, [selectedTlds, activeTld]);

  // Track which TLDs have already been checked so the effect never re-fires
  // for a TLD whose result is already known. Using a ref avoids putting
  // tldAvailability in the useCallback dep array, which previously caused a
  // re-check loop every time any single TLD resolved (H-4 in TECHNICAL_DEBT.md).
  const checkedTldsRef = useRef<Set<string>>(new Set());

  const checkTld = useCallback(
    async (tld: string) => {
      if (checkedTldsRef.current.has(tld)) return;
      checkedTldsRef.current.add(tld);
      try {
        const res = await checkDomain({ name: result.name, tld });
        setTldAvailability((prev) => ({ ...prev, [tld]: res.data.available }));
      } catch {
        setTldAvailability((prev) => ({ ...prev, [tld]: null }));
      }
    },
    [result.name],
  );

  useEffect(() => {
    selectedTlds.forEach((tld) => checkTld(tld));
  }, [selectedTlds, checkTld]);

  const activeAvailable = tldAvailability[activeTld] ?? result.available ?? null;
  const isTaken = activeAvailable === false;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(`${result.name}${activeTld}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleRegister = () => {
    window.open(
      `https://www.namecheap.com/domains/registration/results/?domain=${result.name}${activeTld}`,
      '_blank',
    );
    posthog.capture('register_clicked', { name: result.name, tld: activeTld });
  };

  const displayScore = result.score ? normalizeScore(result.score.total_score) : null;

  // Card state styles
  const cardBorder = favorited
    ? 'border-[#6855e0]/70 ring-1 ring-[#6855e0]/40'
    : isTaken
    ? 'border-[#1c1c2e]/40'
    : 'border-[#1c1c2e]/60 hover:border-[#363658]/80';

  const cardOpacity = isTaken && !favorited ? 'opacity-50 hover:opacity-70' : '';

  if (listView) {
    return (
      <div
        className={`bg-[#111120] rounded-xl border ${cardBorder} ${cardOpacity} flex items-center gap-4 px-4 py-3 transition-all duration-200 animate-fade-in`}
        style={{ animationDelay: `${index * 50}ms` }}
      >
        <span className="flex items-center gap-1.5 text-xs w-20 flex-shrink-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${activeAvailable === true ? 'bg-green-500' : activeAvailable === false ? 'bg-gray-600' : 'bg-gray-700'}`} />
          <span className={activeAvailable === true ? 'text-green-400' : 'text-gray-500'}>
            {activeAvailable === true ? 'Available' : activeAvailable === false ? 'Taken' : '…'}
          </span>
        </span>
        <div className="flex items-baseline gap-1 flex-1 min-w-0">
          <span className="font-mono text-[17px] font-bold text-white truncate">{result.name}</span>
          <span className="font-mono text-[13px] text-indigo-400 font-medium flex-shrink-0">{activeTld}</span>
        </div>
        {displayScore !== null && (
          <span className={`text-sm font-bold flex-shrink-0 ${scoreColor(displayScore)}`}>{displayScore}</span>
        )}
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={activeAvailable === true ? handleRegister : undefined}
            disabled={activeAvailable !== true}
            className={`py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${activeAvailable === true ? 'bg-indigo-600 hover:bg-indigo-500 text-white' : 'bg-[#1e1e35] border border-[#2d2d50] text-gray-300 hover:bg-[#252545]'}`}
          >
            {activeAvailable === true ? 'Register →' : 'Similar names →'}
          </button>
          <div className="relative">
            <button
              onClick={() => setOverflowOpen((v) => !v)}
              className="py-1.5 px-2 rounded-lg border border-[#2a2a45] text-gray-500 hover:text-gray-300 hover:border-[#4a4a70] transition-all"
            >
              <LuEllipsis className="w-4 h-4" />
            </button>
            {overflowOpen && (
              <OverflowMenu
                result={result}
                activeTld={activeTld}
                onDismiss={() => { setOverflowOpen(false); onDismiss(); }}
                onClose={() => setOverflowOpen(false)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bg-[#111120] rounded-xl border ${cardBorder} ${cardOpacity} flex flex-col p-4 transition-all duration-200 group relative animate-fade-in hover:border-[#363658]`}
      style={{ animationDelay: `${index * 50}ms` }}
    >
      {/* Row 1: availability dot + save star */}
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-1.5 text-xs font-medium">
          <span
            className={`w-2 h-2 rounded-full flex-shrink-0 ${
              activeAvailable === true ? 'bg-green-500' : activeAvailable === false ? 'bg-gray-600' : 'bg-gray-700'
            }`}
          />
          <span className={activeAvailable === true ? 'text-green-400' : activeAvailable === false ? 'text-gray-500' : 'text-gray-600'}>
            {activeAvailable === true ? 'Available' : activeAvailable === false ? 'Taken' : 'Checking…'}
          </span>
        </span>
        <button
          onClick={() => toggleFavorite(result.name)}
          className="text-gray-600 hover:text-indigo-400 transition-colors"
          title={favorited ? 'Remove from saved' : 'Save'}
        >
          {favorited ? (
            <HiStar className="w-4 h-4 text-indigo-400" />
          ) : (
            <HiOutlineStar className="w-4 h-4" />
          )}
        </button>
      </div>

      {/* Row 2: domain name + copy (on hover) */}
      <div className="flex items-baseline gap-1 mb-1 relative">
        <span className="font-mono text-[19px] font-bold text-white leading-tight">{result.name}</span>
        <span className="font-mono text-[13px] text-indigo-400 font-medium">{activeTld}</span>
        <button
          onClick={handleCopy}
          className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity text-gray-600 hover:text-gray-400"
          title="Copy"
        >
          {copied ? <LuCheck className="w-3.5 h-3.5 text-green-400" /> : <LuCopy className="w-3.5 h-3.5" />}
        </button>
      </div>

      {/* TLD chips (only when multiple selected) */}
      {selectedTlds.length > 1 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {selectedTlds.map((tld) => {
            const avail = tldAvailability[tld];
            return (
              <button
                key={tld}
                onClick={() => setActiveTld(tld)}
                className={`font-mono text-[11px] px-1.5 py-0.5 rounded border transition-all ${
                  tld === activeTld
                    ? 'border-indigo-500/70 bg-[#251c5c]/50 text-[#b8b0f8]'
                    : 'border-[#1c1c2e] text-gray-500 hover:border-[#363658]'
                }`}
              >
                {tld}
                {avail === true && <span className="ml-0.5 text-green-500">·</span>}
                {avail === false && <span className="ml-0.5 text-gray-600">·</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* Row 3: BRAND SCORE */}
      {displayScore !== null && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] font-semibold tracking-widest text-gray-600 uppercase">Brand Score</span>
            <span className={`text-sm font-bold ${scoreColor(displayScore)}`}>{displayScore}</span>
          </div>
          <div className="h-[3px] rounded-full bg-[#181828] overflow-hidden">
            <div
              className={`h-full rounded-full ${scoreBarColor(displayScore)} transition-all duration-700`}
              style={{ width: `${displayScore}%` }}
            />
          </div>
        </div>
      )}

      {/* Row 4: USPTO status */}
      <div className="mb-4">
        <UsptoStatus available={result.trademarkAvailable} />
      </div>

      {/* Row 5: action row — Register/Similar + ··· only (no Score button) */}
      <div className="flex gap-2 mt-auto">
        <button
          onClick={activeAvailable === true ? handleRegister : undefined}
          disabled={activeAvailable === null}
          className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all ${
            activeAvailable === true
              ? 'bg-indigo-600 hover:bg-indigo-500 text-white shadow-lg shadow-indigo-900/30'
              : activeAvailable === false
              ? 'bg-[#1e1e35] border border-[#2d2d50] text-gray-300 hover:bg-[#252545] hover:text-gray-200'
              : 'bg-[#131320] text-gray-600 cursor-default'
          }`}
        >
          {activeAvailable === true ? 'Register →' : 'Similar names →'}
        </button>

        <div className="relative">
          <button
            onClick={() => setOverflowOpen((v) => !v)}
            className="py-1.5 px-2.5 rounded-lg border border-[#2a2a45] text-gray-500 hover:border-[#4a4a70] hover:text-gray-300 transition-all"
            title="More options"
          >
            <LuEllipsis className="w-4 h-4" />
          </button>
          {overflowOpen && (
            <OverflowMenu
              result={result}
              activeTld={activeTld}
              onDismiss={() => { setOverflowOpen(false); onDismiss(); }}
              onClose={() => setOverflowOpen(false)}
            />
          )}
        </div>
      </div>

      {modalOpen && (
        <NameScoreModal result={result} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
};
